import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, LayoutGrid, Newspaper, Search, Star, X } from 'lucide-react';
import { buildResourceMenu, pathToPage, splitHighlight, visibleRows } from '../../utils/resourceMenu.mjs';

const EXPANDED_KEY = 'arn-expanded';
const readExpanded = () => {
  try { return new Set(JSON.parse(sessionStorage.getItem(EXPANDED_KEY) || '[]')); } catch { return new Set(); }
};

const Highlight = ({ text, query }) => splitHighlight(text, query).map((part, index) => (
  part.match ? <mark key={index}>{part.text}</mark> : <React.Fragment key={index}>{part.text}</React.Fragment>
));

const ResourceMenu = ({ categories, articles, currentSlug, query, onQuery, onNavigate, searchRef, replace = false }) => {
  const menu = useMemo(() => buildResourceMenu(categories, articles, { query }), [categories, articles, query]);
  const fullMenu = useMemo(() => buildResourceMenu(categories, articles), [categories, articles]);
  const currentPath = useMemo(() => pathToPage(fullMenu, currentSlug), [fullMenu, currentSlug]);
  const [expanded, setExpanded] = useState(() => new Set([...readExpanded(), ...currentPath]));
  const [showAll, setShowAll] = useState(() => new Set());
  const searching = Boolean(query.trim());
  const scrollRef = useRef(null);

  useEffect(() => {
    const scroller = scrollRef.current;
    const current = scroller?.querySelector('.arn-item.current:not(.all)');
    if (!scroller || !current || searching) return;
    const head = current.closest('.arn-cat')?.querySelector('.arn-cat-head');
    const room = scroller.clientHeight - 24;
    const fitsUnderHeader = head && current.offsetTop + current.offsetHeight - head.offsetTop <= room;
    scroller.scrollTop = Math.max(0, fitsUnderHeader ? head.offsetTop - 8 : current.offsetTop - (scroller.clientHeight - current.offsetHeight) / 2);
  }, [currentSlug, currentPath.length, searching]);

  useEffect(() => {
    if (!currentPath.length) return;
    setExpanded(previous => (currentPath.every(id => previous.has(id)) ? previous : new Set([...previous, ...currentPath])));
  }, [currentPath]);
  useEffect(() => {
    try { sessionStorage.setItem(EXPANDED_KEY, JSON.stringify([...expanded])); } catch { /* storage unavailable */ }
  }, [expanded]);

  const toggle = id => setExpanded(previous => {
    const next = new Set(previous);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const revealAll = (id, event) => {
    const body = event.currentTarget.closest('.arn-cat-body');
    const before = body ? body.querySelectorAll('.arn-item').length : 0;
    setShowAll(previous => new Set(previous).add(id));
    requestAnimationFrame(() => body?.querySelectorAll('.arn-item')[before]?.focus());
  };

  const pageLink = (page, className = '') => {
    const current = page.slug === currentSlug;
    return (
      <Link
        key={page.id}
        to={`/agent/resources/${page.slug}`}
        replace={replace}
        onClick={onNavigate}
        className={`arn-item ${className} ${current ? 'current' : ''}`.trim()}
        aria-current={current ? 'page' : undefined}
      >
        <span className="arn-item-text"><Highlight text={page.title} query={query} /></span>
        {Boolean(page.is_featured) && <Star size={13} className="arn-star" fill="currentColor" aria-label="Featured" />}
      </Link>
    );
  };

  const renderNode = (node, depth = 0) => {
    const open = searching || expanded.has(node.id);
    const { rows, hidden } = visibleRows(node, { showAll: showAll.has(node.id), currentSlug, searching });
    const bodyId = `arn-cat-${node.id}`;
    return (
      <div className="arn-cat" key={node.id} style={{ '--depth': depth }}>
        <button type="button" className="arn-cat-head" aria-expanded={open} aria-controls={bodyId} onClick={() => !searching && toggle(node.id)}>
          {open ? <ChevronDown size={18} aria-hidden="true" /> : <ChevronRight size={18} aria-hidden="true" />}
          <span className="arn-cat-name">{node.name}</span>
          <span className="arn-count">{node.count}</span>
        </button>
        {open && (
          <div className="arn-cat-body" id={bodyId}>
            {!!node.featured.length && (
              <>
                <div className="arn-label"><Star size={12} fill="currentColor" aria-hidden="true" /> Featured</div>
                {node.featured.map(page => pageLink(page))}
              </>
            )}
            {!!node.rest.length && (
              <>
                {!!node.featured.length && <div className="arn-label">A &ndash; Z</div>}
                {rows.map(page => pageLink(page))}
                {hidden > 0 && <button type="button" className="arn-more" onClick={event => revealAll(node.id, event)}>Show all {node.featured.length + node.rest.length}</button>}
              </>
            )}
            {node.children.length > 0 && <div className="arn-sub">{node.children.map(child => renderNode(child, depth + 1))}</div>}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="arn-menu">
      <div className="arn-search-wrap">
        <label className="arn-search">
          <Search size={18} aria-hidden="true" />
          <input
            ref={searchRef}
            type="search"
            value={query}
            onChange={event => onQuery(event.target.value)}
            placeholder="Search suppliers & resources"
            aria-label="Search the resource menu"
            autoComplete="off"
          />
          {query && <button type="button" className="arn-clear" aria-label="Clear search" onClick={() => onQuery('')}><X size={16} /></button>}
        </label>
        {searching && <p className="arn-results" role="status">{menu.total} {menu.total === 1 ? 'result' : 'results'} for &ldquo;{query.trim()}&rdquo;</p>}
      </div>
      <nav className="arn-scroll" aria-label="Agent Resources" ref={scrollRef}>
        {!searching && (
          <Link to="/agent/resources" replace={replace} onClick={onNavigate} className={`arn-item all ${currentSlug ? '' : 'current'}`.trim()} aria-current={currentSlug ? undefined : 'page'}>
            <LayoutGrid size={18} aria-hidden="true" /><span className="arn-item-text">All resources</span>
          </Link>
        )}
        {!!menu.feeds.length && (
          <>
            <div className="arn-label top">News &amp; Promotions</div>
            {menu.feeds.map(page => {
              const current = page.slug === currentSlug;
              return (
                <Link key={page.id} to={`/agent/resources/${page.slug}`} replace={replace} onClick={onNavigate} className={`arn-item feed ${current ? 'current' : ''}`.trim()} aria-current={current ? 'page' : undefined}>
                  <Newspaper size={18} aria-hidden="true" /><span className="arn-item-text"><Highlight text={page.title} query={query} /></span>
                </Link>
              );
            })}
          </>
        )}
        {!!menu.categories.length && <div className="arn-label top">Browse by category</div>}
        {menu.categories.map(node => renderNode(node))}
        {searching && menu.total === 0 && <p className="arn-empty">No matches. Try a supplier name.</p>}
      </nav>
    </div>
  );
};

export default ResourceMenu;
